// What the design views keep in the address, so a reload or a shared link opens the same view and
// Back steps out of a drawer or a flow instead of leaving the page. The pages read window.location
// themselves (they are ISR), so these take and return a query string.
import { DESIGN_CATEGORIES } from './design-categories';
import { enumParam, readParams, writeParams, type UrlCodec } from '@/lib/url-state';

const DESIGN_SORTS = ['coverage', 'rank', 'revenue', 'downloads', 'name'] as const;
export type DesignSort = (typeof DESIGN_SORTS)[number];

type DesignsParams = { query: string; pattern: string; category: string; sort: DesignSort; app: string | null };

const SLUGS: string[] = DESIGN_CATEGORIES.map((c) => c.slug);
const isCategory = (value: string | null): value is string => value !== null && SLUGS.includes(value);

const PATTERN = enumParam('category', ['all', ...SLUGS], 'all');
const SORT = enumParam('sort', DESIGN_SORTS, 'coverage');

/** /designs: q (search), category (design category), genre (App Store category), sort, app (Quick look). */
const DESIGNS: UrlCodec<DesignsParams> = {
  keys: ['q', 'category', 'genre', 'sort', 'app'],
  parse: (raw) => ({ query: raw.q ?? '', pattern: PATTERN.parse(raw), category: raw.genre || 'all', sort: SORT.parse(raw), app: raw.app || null }),
  serialize: (v) => ({
    q: v.query.trim() || null,
    ...PATTERN.serialize(v.pattern),
    genre: v.category === 'all' ? null : v.category || null,
    ...SORT.serialize(v.sort),
    app: v.app || null,
  }),
};

const toSearch = (p: URLSearchParams) => (p.size ? `?${p}` : '');

export const readDesignsParams = (search: string): DesignsParams => readParams(new URLSearchParams(search), DESIGNS);

/** The query string for a /designs view; defaults are left out and other parameters kept. */
export const designsSearch = (search: string, view: DesignsParams): string => toSearch(writeParams(new URLSearchParams(search), DESIGNS, view));

/** App page "Inside the app": cat (a design category or motion) and flow (its name, only with a category). */
const SCREENS: UrlCodec<{ cat: string | null; flow: string | null }> = {
  keys: ['cat', 'flow'],
  parse: (raw) => {
    const cat = raw.cat === 'motion' || isCategory(raw.cat) ? raw.cat : null;
    return { cat, flow: (isCategory(cat) && raw.flow) || null };
  },
  serialize: (v) => ({ cat: v.cat || null, flow: (v.cat && v.flow) || null }),
};

/** `shown` lists the views this app has; any other cat reads as none, so the page opens on All instead of an empty view. */
export function readScreensParams(search: string, shown?: readonly string[]): { cat: string | null; flow: string | null } {
  const view = readParams(new URLSearchParams(search), SCREENS);
  return view.cat && shown && !shown.includes(view.cat) ? { cat: null, flow: null } : view;
}

export const screensSearch = (search: string, cat: string | null, flow: string | null): string =>
  toSearch(writeParams(new URLSearchParams(search), SCREENS, { cat, flow }));
